import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { el, enUS } from 'date-fns/locale';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateConstraints } from '@/utils/date';
import { RESERVATION_CONSTRAINTS } from '@/types';
import { InputAdornment } from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';

const dateLocales = {
  en: enUS,
  el: el,
};

interface DatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  label?: string;
  disabled?: boolean;
}

export function DatePicker({ value, onChange, label, disabled = false }: DatePickerProps) {
  const { t, i18n } = useTranslation();
  const displayLabel = label ?? t('datePicker.label');
  const currentLang = i18n.language.split('-')[0] as keyof typeof dateLocales;
  const dateLocale = dateLocales[currentLang] || enUS;
  const [isOpen, setIsOpen] = useState(false);

  const { minDate, maxDate } = getDateConstraints(
    RESERVATION_CONSTRAINTS.MIN_DATE_OFFSET,
    RESERVATION_CONSTRAINTS.MAX_DATE_OFFSET
  );

  // Default to current date if value is null
  const effectiveValue = value ?? new Date();

  return (
    <LocalizationProvider key={currentLang} dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
      <MuiDatePicker
        label={displayLabel}
        value={effectiveValue}
        onChange={onChange}
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        format="dd/MM/yyyy"
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        slots={{
          openPickerIcon: () => null,
        }}
        slotProps={{
          textField: {
            fullWidth: true,
            variant: 'outlined',
            onClick: () => !disabled && setIsOpen(true),
            inputProps: {
              readOnly: true, // Disable typing, only allow date selection
            },
            InputProps: {
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarMonth />
                </InputAdornment>
              ),
            },
          },
        }}
      />
    </LocalizationProvider>
  );
}
